/**
 *
 * We could sometimes have multiple changes to the region content happening in a short space of time.
 *
 * This can cause issues for some screen readers and may cause some of the messages to be swallowed and not read.
 *
 * To solve this we borrow from nga11y. If we duplicate the live region and alternate changes between the two regions, it fixes this problem.
 */

import React, { useEffect, useRef, useState } from 'react'

import MessageBlock from './MessageBlock'

interface AnnouncerProps {
  politeMessage?: string
  assertiveMessage?: string
}

const Announcer: React.FC<AnnouncerProps> = ({
  politeMessage,
  assertiveMessage
}) => {
  const [state, setState] = useState({
    assertiveMessage1: '',
    assertiveMessage2: '',
    politeMessage1: '',
    politeMessage2: ''
  })

  const alternatePoliteRef = useRef(false)
  const alternateAssertiveRef = useRef(false)

  useEffect(() => {
    if (politeMessage !== undefined) {
      setState((prevState) => ({
        ...prevState,
        politeMessage1: alternatePoliteRef.current ? '' : politeMessage,
        politeMessage2: alternatePoliteRef.current ? politeMessage : ''
      }))
      alternatePoliteRef.current = !alternatePoliteRef.current
    }
  }, [politeMessage])

  useEffect(() => {
    if (assertiveMessage !== undefined) {
      setState((prevState) => ({
        ...prevState,
        assertiveMessage1: alternateAssertiveRef.current
          ? ''
          : assertiveMessage,
        assertiveMessage2: alternateAssertiveRef.current ? assertiveMessage : ''
      }))
      alternateAssertiveRef.current = !alternateAssertiveRef.current
    }
  }, [assertiveMessage])

  const {
    assertiveMessage1,
    assertiveMessage2,
    politeMessage1,
    politeMessage2
  } = state

  return (
    <>
      <MessageBlock aria-live="assertive" message={assertiveMessage1} />
      <MessageBlock aria-live="assertive" message={assertiveMessage2} />
      <MessageBlock aria-live="polite" message={politeMessage1} />
      <MessageBlock aria-live="polite" message={politeMessage2} />
    </>
  )
}

export default Announcer
